import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Settings2, RotateCcw } from 'lucide-react';

/**
 * KpiCustomizerDrawer
 * --------------------
 * Drawer permettant au client de choisir quels KPIs afficher sur le dashboard.
 * Persiste la sélection en localStorage (clé `client_dashboard_kpi_visibility`).
 *
 * Utilisation :
 *   const [visibility, KpiBtn] = useKpiVisibility(KPI_DEFINITIONS);
 *   {visibility.total && <KpiCard ... />}
 *   {KpiBtn}
 */

const STORAGE_KEY = 'client_dashboard_kpi_visibility_v1';

export const useKpiVisibility = (definitions) => {
  // definitions: [{ key, label, defaultVisible: bool, description }]
  const defaultMap = definitions.reduce((acc, d) => {
    acc[d.key] = d.defaultVisible !== false;
    return acc;
  }, {});

  const [visibility, setVisibility] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // merge avec defaults pour gérer l'ajout de nouveaux KPIs
        return { ...defaultMap, ...parsed };
      }
    } catch (e) {
      // fallback default
    }
    return defaultMap;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
    } catch (e) {
      // ignore quota
    }
  }, [visibility]);

  const toggle = (key) => setVisibility((v) => ({ ...v, [key]: !v[key] }));
  const reset = () => setVisibility(defaultMap);

  return [visibility, toggle, reset];
};

export const KpiCustomizerDrawer = ({ definitions, visibility, onToggle, onReset, triggerSize = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const visibleCount = Object.values(visibility).filter(Boolean).length;
  const totalCount = definitions.length;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size={triggerSize}
          className="gap-2"
          data-testid="customize-kpi-btn"
        >
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Personnaliser</span>
          <span className="text-xs text-slate-400">({visibleCount}/{totalCount})</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md" data-testid="kpi-customizer-drawer">
        <SheetHeader>
          <SheetTitle>Personnaliser le tableau de bord</SheetTitle>
          <SheetDescription>
            Choisissez les indicateurs à afficher. Vos préférences sont enregistrées localement.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-2">
          {definitions.map((def) => {
            const Icon = def.icon;
            return (
              <div
                key={def.key}
                className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`kpi-toggle-${def.key}`}
              >
                {Icon && (
                  <div className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${def.iconBg || 'bg-slate-100'}`}>
                    <Icon className={`w-4 h-4 ${def.iconColor || 'text-slate-600'}`} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">{def.label}</p>
                  {def.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{def.description}</p>
                  )}
                </div>
                <Switch
                  checked={!!visibility[def.key]}
                  onCheckedChange={() => onToggle(def.key)}
                  data-testid={`kpi-switch-${def.key}`}
                />
              </div>
            );
          })}
        </div>

        <SheetFooter className="mt-6 flex-row sm:justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="gap-2 text-slate-500 hover:text-slate-700"
            data-testid="kpi-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Réinitialiser
          </Button>
          <Button
            size="sm"
            onClick={() => setOpen(false)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="kpi-apply-btn"
          >
            Terminé
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default KpiCustomizerDrawer;
